import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FaChartBar, FaUsers, FaClock, FaShoppingCart, FaGift, FaCalendar, FaTag } from 'react-icons/fa';

const SegmentacionClientes = () => {
    const { marcaId } = useParams();
    const [rfmData, setRfmData] = useState(null);
    const [behaviorSegments, setBehaviorSegments] = useState({});
    const [loading, setLoading] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('12');
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [segmentStrategies, setSegmentStrategies] = useState(null);

    useEffect(() => {
        if (marcaId) {
            fetchEnhancedSegmentation();
        }
    }, [marcaId, selectedPeriod]);

    const fetchEnhancedSegmentation = async () => {
        setLoading(true);
        try {
            // Obtener datos RFM mejorados con segmentos de comportamiento
            const enhancedData = await get(`clientes/analytics/enhanced-segments?period=${selectedPeriod}&marcaId=${marcaId}`);
            setRfmData(enhancedData);

            // Procesar segmentos de comportamiento
            const behaviorData = {};
            enhancedData.rfmData?.forEach(customer => {
                if (customer.additionalSegments) {
                    customer.additionalSegments.forEach(segment => {
                        if (!behaviorData[segment]) {
                            behaviorData[segment] = [];
                        }
                        behaviorData[segment].push(customer);
                    });
                }
            });
            setBehaviorSegments(behaviorData);
        } catch (error) {
            console.error('Error fetching enhanced segmentation:', error);
            // Fallback a segmentación básica si falla
            try {
                const basicData = await get(`clientes/segmentacion/${marcaId}`);
                setRfmData({ segments: basicData });
            } catch (fallbackError) {
                Swal.fire('Error', fallbackError.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSegmentStrategies = async (segmentName) => {
        try {
            const strategies = await get(`clientes/analytics/segment-strategies/${segmentName}`);
            setSegmentStrategies(strategies);
            setSelectedSegment(segmentName);
        } catch (error) {
            console.error('Error fetching strategies:', error);
        }
    };

    const getSegmentColor = (segmentName) => {
        const colors = {
            'Champions': 'bg-green-100 text-green-800',
            'Loyal Customers': 'bg-blue-100 text-blue-800',
            'At Risk': 'bg-yellow-100 text-yellow-800',
            'Can\'t Lose': 'bg-red-100 text-red-800',
            'About to Sleep': 'bg-orange-100 text-orange-800',
            'Lost': 'bg-gray-100 text-gray-800',
            'Fin de semana': 'bg-purple-100 text-purple-800',
            'Nocturnos': 'bg-indigo-100 text-indigo-800',
            'Cazapromos': 'bg-pink-100 text-pink-800',
            'Ticket alto': 'bg-emerald-100 text-emerald-800',
            'Express': 'bg-cyan-100 text-cyan-800',
            'Recurrentes': 'bg-teal-100 text-teal-800',
            'Exploradores': 'bg-amber-100 text-amber-800',
            'Especialistas': 'bg-violet-100 text-violet-800'
        };
        return colors[segmentName] || 'bg-gray-100 text-gray-800';
    };

    const calculateMetrics = () => {
        if (!rfmData?.rfmData) return null;

        const customers = rfmData.rfmData;
        return {
            totalCustomers: customers.length,
            avgBasketSize: customers.reduce((sum, c) => sum + (c.avg_basket_size || 0), 0) / customers.length,
            avgFrequency: customers.reduce((sum, c) => sum + c.frequency, 0) / customers.length,
            avgRecency: customers.reduce((sum, c) => sum + c.recency, 0) / customers.length,
            avgPurchaseInterval: customers.reduce((sum, c) => sum + (c.ipi_avg || 0), 0) / customers.length,
            avgPromoUsage: customers.reduce((sum, c) => sum + (c.promo_rate || 0), 0) / customers.length
        };
    };

    if (loading) {
        return (
            <div className="container mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-lg">Cargando segmentación avanzada...</span>
                </div>
            </div>
        );
    }

    const metrics = calculateMetrics();

    return (
        <div className="container mx-auto p-6">
            <div className="mb-6">
                <h1 className="text-4xl font-bold mb-4 flex items-center">
                    <FaChartBar className="mr-3 text-blue-500" />
                    Segmentación Avanzada de Clientes
                </h1>

                {/* Selector de período */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Período de análisis:
                    </label>
                    <select
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2"
                    >
                        <option value="3">3 meses</option>
                        <option value="6">6 meses</option>
                        <option value="12">12 meses</option>
                        <option value="24">24 meses</option>
                    </select>
                </div>

                {/* Métricas generales */}
                {metrics && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="flex items-center">
                                <FaUsers className="text-blue-500 mr-2" />
                                <span className="font-semibold">Total Clientes</span>
                            </div>
                            <p className="text-2xl font-bold text-blue-600">{metrics.totalCustomers}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="flex items-center">
                                <FaShoppingCart className="text-green-500 mr-2" />
                                <span className="font-semibold">Ticket Promedio</span>
                            </div>
                            <p className="text-2xl font-bold text-green-600">${metrics.avgBasketSize.toFixed(2)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="flex items-center">
                                <FaClock className="text-orange-500 mr-2" />
                                <span className="font-semibold">Frecuencia Promedio</span>
                            </div>
                            <p className="text-2xl font-bold text-orange-600">{metrics.avgFrequency.toFixed(1)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="flex items-center">
                                <FaCalendar className="text-purple-500 mr-2" />
                                <span className="font-semibold">Intervalo Promedio</span>
                            </div>
                            <p className="text-2xl font-bold text-purple-600">{metrics.avgPurchaseInterval.toFixed(1)} días</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border">
                            <div className="flex items-center">
                                <FaGift className="text-pink-500 mr-2" />
                                <span className="font-semibold">Uso de Promociones</span>
                            </div>
                            <p className="text-2xl font-bold text-pink-600">{(metrics.avgPromoUsage * 100).toFixed(1)}%</p>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Segmentos RFM */}
                <div className="bg-white rounded-lg shadow border">
                    <div className="p-4 border-b">
                        <h2 className="text-2xl font-semibold flex items-center">
                            <FaTag className="mr-2 text-blue-500" />
                            Segmentos RFM
                        </h2>
                    </div>
                    <div className="p-4">
                        {rfmData?.segments ? Object.entries(rfmData.segments).map(([segmentName, customers]) => (
                            <div key={segmentName} className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-medium">{segmentName}</h3>
                                    <span className={`px-2 py-1 rounded-full text-sm ${getSegmentColor(segmentName)}`}>
                                        {customers.length} clientes
                                    </span>
                                </div>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {customers.slice(0, 5).map(cliente => (
                                        <div key={cliente._id} className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                                            {cliente.nombre} {cliente.apellido}
                                        </div>
                                    ))}
                                    {customers.length > 5 && (
                                        <div className="text-sm text-gray-500 italic">
                                            ... y {customers.length - 5} más
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => fetchSegmentStrategies(segmentName)}
                                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                >
                                    Ver estrategias →
                                </button>
                            </div>
                        )) : (
                            <p className="text-gray-500">No hay datos de segmentación RFM disponibles</p>
                        )}
                    </div>
                </div>

                {/* Segmentos de Comportamiento */}
                <div className="bg-white rounded-lg shadow border">
                    <div className="p-4 border-b">
                        <h2 className="text-2xl font-semibold flex items-center">
                            <FaUsers className="mr-2 text-green-500" />
                            Segmentos de Comportamiento
                        </h2>
                    </div>
                    <div className="p-4">
                        {Object.keys(behaviorSegments).length > 0 ? Object.entries(behaviorSegments).map(([segmentName, customers]) => (
                            <div key={segmentName} className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-medium">{segmentName}</h3>
                                    <span className={`px-2 py-1 rounded-full text-sm ${getSegmentColor(segmentName)}`}>
                                        {customers.length} clientes
                                    </span>
                                </div>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {customers.slice(0, 5).map(customer => (
                                        <div key={customer.clienteId} className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                                            {customer.nombre} - RFM: {customer.rfmScore}
                                        </div>
                                    ))}
                                    {customers.length > 5 && (
                                        <div className="text-sm text-gray-500 italic">
                                            ... y {customers.length - 5} más
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => fetchSegmentStrategies(segmentName)}
                                    className="mt-2 text-green-600 hover:text-green-800 text-sm"
                                >
                                    Ver estrategias →
                                </button>
                            </div>
                        )) : (
                            <p className="text-gray-500">No hay segmentos de comportamiento disponibles</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Estrategias */}
            {segmentStrategies && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-96 overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">{segmentStrategies.name}</h3>
                            <button
                                onClick={() => setSegmentStrategies(null)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        <p className="text-gray-600 mb-4">{segmentStrategies.description}</p>
                        <div className="mb-4">
                            <span className={`px-3 py-1 rounded-full text-sm ${segmentStrategies.priority === 'high' ? 'bg-red-100 text-red-800' :
                                    segmentStrategies.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-green-100 text-green-800'
                                }`}>
                                Prioridad: {segmentStrategies.priority}
                            </span>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-2">Estrategias Recomendadas:</h4>
                            <ul className="list-disc list-inside space-y-1">
                                {segmentStrategies.strategies.map((strategy, index) => (
                                    <li key={index} className="text-gray-700">{strategy}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SegmentacionClientes;
